import classNames from "classnames";
import _values from "lodash/values";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";

import { getKioskDeviceID, getKioskName } from "candidate/Kiosk/util";
import Card from "common/Card";
import CardGroup from "common/CardGroup";
import SVGIcon from "common/SVGIcon";
import Question from "common/Question";
import Loader from "common/Loader";
import { QUESTION_FLOW, QUESTION_TYPE, REQUEST_QUEUE_KEY } from "lib/constants";
import { userShape } from "lib/propTypes";
import { generateRequest, getQueue } from "redux/fetch-queue";
import { questionAnswer, questionNext, questionUndo } from "redux/questions/actions";
import { answeredQuestions, getCurrentQuestion, localQuestions } from "redux/questions/selectors";
import { uiSetAsyncError } from "redux/ws/actions";
import { rollbarXHRError } from "lib/rollbar";
import { getUserLanguage } from "redux/ws/selectors";
import { createSelector } from "reselect";

const selector = createSelector(
    (state) => state.answers,
    (state) => state.questions.questions,
    (state) => getCurrentQuestion(state, getUserLanguage(state)),
    (state) => localQuestions(state, getUserLanguage(state)),
    (state) => state.ws.ui.asyncError,
    (state) => state.ws.ui.submitting,
    (answers, questions, question, allQuestions, asyncError, submitting) => ({
        answers,
        allQuestions,
        asyncError,
        isCompleted: answeredQuestions(questions).length === questions.length,
        // Even though we're displaying a single question, you send it as an
        // array so that the css transitions can work properly. (The React will
        // detect the change in the array and transition the missing item out
        // and the new one in.)
        questions: question ? [question] : [],
        submitting,
    }),
);

const Questions = (
    {
        autoSelect,
        canSkip,
        canUndo,
        className,
        extraData,
        flow,
        handleCompleted,
        renderEmpty,
        source,
        renderLanguageSelect,
        toggleAnonymous,
        handleAnswerResponse,
        isLastQuestion,
        isAnonymous,
    },
    { t: _ },
) => {
    const { answers, allQuestions, asyncError, isCompleted, questions, submitting } =
        useSelector(selector);
    const dispatch = useDispatch();

    const [canUndoState, setCanUndoState] = useState(false);
    const [currentAnswer, setCurrentAnswer] = useState(null);
    const [lastAnswer, setLastAnswer] = useState(null);
    const [lastAnswerTimeout, setLastAnswerTimeout] = useState(null);
    const [preSubmit, setPreSubmit] = useState(null);
    const [requestQueue, setRequestQueue] = useState(null);

    const isKiosk = useSelector((state) => state.isKiosk);
    const kioskUUID = useSelector((state) => state.kioskHireUUID);
    const language = useSelector((state) => state.i18nState.lang);
    const isPreview = useSelector((state) => state.is_preview);

    // Effects
    // Got some kinda error, so don't act like they answered
    useEffect(() => {
        if (asyncError) {
            setLastAnswer(null);
        }
    }, [asyncError]);

    // call handleCompleted when everything is done
    useEffect(() => {
        if (isCompleted && handleCompleted) {
            handleCompleted(allQuestions);
        }
    }, [isCompleted, handleCompleted]);

    useEffect(() => {
        if (isKiosk) {
            setRequestQueue(getQueue(REQUEST_QUEUE_KEY.KIOSK_ANSWERS));
        }

        // Make sure when unmounting we clear the timeout
        return () => clearTimeout(lastAnswerTimeout);
    }, []);

    // Handlers
    const determineQuestion = (question) => {
        let q = question;
        if (!q) {
            if (questions.length === 0) {
                return false;
            }

            q = questions[0];
        }

        return q;
    };

    const canUndoQuestion = (question) => {
        const q = determineQuestion(question);
        if (!q) {
            return false;
        }

        return autoSelect && !String(q.id).startsWith("pref_");
    };

    const isAutoSelect = (question) => {
        const q = determineQuestion(question);
        if (!q) {
            return false;
        }

        return canUndoQuestion(q) && q.type !== QUESTION_TYPE.CHOOSE_MANY;
    };

    const processKioskAnswer = (answer) => {
        const data = {
            follow_up_response: answer.followUp || null,
            kiosk_name: getKioskName(),
            device_id: getKioskDeviceID(),
            kiosk_uuid: kioskUUID,
            question_id: answer.questionID,
            response: answer.response,
            source,
            time_created: moment.utc().toISOString(),
            language,
            ...extraData,
        };
        requestQueue
            .add(generateRequest("POST", "/api/hire/v1/answer/kiosk/", { body: data }))
            .then(handleAnswerResponse)
            .catch((err) => {
                // If we get a rejection from the queue then it's most likely we received a failure
                // from the API and thus have connectivity and can submit this error to rollbar
                rollbarXHRError("Queued Kiosk Answer Failed", err, data);
            });
        dispatch(questionNext());
    };

    const processAnswer = (answer, questionPreSubmit) => {
        setCurrentAnswer(null);
        setPreSubmit(null);

        // Some questions might need to do a little data massage before we submit
        // the answer, so here we let them return a promise that resolves when
        // they're ready for processing.
        if (questionPreSubmit) {
            questionPreSubmit(answer).then((newAnswer) => {
                processAnswer(newAnswer);
            });
            return;
        }

        // If in kiosk mode then queue the request and move on to the next question
        if (isKiosk) {
            processKioskAnswer(answer);
            return;
        }

        dispatch(
            questionAnswer({
                ...answer,
                ...extraData,
                flow,
                source,
            }),
        );

        if (canSkip || canUndo) {
            setCanUndoState(canUndo && canUndoQuestion());
            setLastAnswer(answer);
            clearTimeout(lastAnswerTimeout);
            setLastAnswerTimeout(setTimeout(() => setLastAnswer(null), 5000));
        }
    };

    const onSkip = (event) => {
        event.preventDefault();
        clearTimeout(lastAnswerTimeout);

        // Answer will be set if they hit skip during an optional followup
        if (currentAnswer) {
            processAnswer(currentAnswer);
        } else {
            dispatch(questionNext());
        }
        if (toggleAnonymous) {
            toggleAnonymous(true);
        }
    };

    const onUndo = (event) => {
        event.preventDefault();

        if (!lastAnswer) {
            return;
        }

        const { questionID } = lastAnswer;

        setLastAnswer(null);

        clearTimeout(lastAnswerTimeout);
        if (toggleAnonymous) {
            toggleAnonymous(true);
        }
        dispatch(questionUndo(questionID));
    };

    const onNext = (event) => {
        event.preventDefault();

        processAnswer(currentAnswer, preSubmit);
    };

    const onDismissError = () => {
        dispatch(uiSetAsyncError(null));
    };

    const handleAnswer = ({
        questionID,
        response,
        responseText,
        followUp,
        preSubmit: questionPreSubmit,
        selfIdentified = false,
        canSubmit = true,
    }) => {
        const answer = {
            questionID,
            response,
            responseText,
            followUp,
            selfIdentified,
        };

        if (isAutoSelect()) {
            processAnswer(answer, questionPreSubmit);
        } else {
            setPreSubmit(() => questionPreSubmit);
            setCurrentAnswer(canSubmit ? answer : null);
        }
    };

    /**
     * In a case where someone selects an answer that has an optional followup,
     * we want to ensure we capture their response even if they end up clicking
     * "skip" to bypass answering the followup.
     */
    const handleFollowUpStart = (answer) => {
        setCurrentAnswer(answer);
    };

    // Renderers
    const renderUndo = () => {
        if (!canUndoState || !lastAnswer || submitting || asyncError || isPreview) {
            return null;
        }

        return (
            <CSSTransition mountOnEnter unmountOnExit classNames="fadeout" timeout={200}>
                <a href="#" className="questions__undo" onClick={onUndo}>
                    <SVGIcon icon="undo" />
                    <span>{_("Undo")}</span>
                </a>
            </CSSTransition>
        );
    };

    const localRenderEmpty = () => {
        let content;
        if (renderEmpty) {
            content = renderEmpty();
        } else {
            content = (
                <Card className="portal-dialog portal-dialog-card questions__card">
                    <div className="questions__empty">
                        You've answered all the questions we have right now&hellip; check back
                        later!
                    </div>
                </Card>
            );
        }

        return (
            <CSSTransition
                mountOnEnter
                unmountOnExit
                classNames="fadeout"
                timeout={{ enter: 100, exit: 0 }}
            >
                {content}
            </CSSTransition>
        );
    };

    const renderQuestions = () => {
        if (
            (answers && Object.keys(answers).length === allQuestions.length) ||
            questions.length === 0
        ) {
            return localRenderEmpty();
        }

        return questions.map((question) => (
            <CSSTransition
                key={question.id}
                mountOnEnter
                classNames="slideleft"
                timeout={{
                    enter: 0,
                    exit: 500,
                }}
            >
                <Card className="portal-dialog portal-dialog-card questions__card">
                    <Question
                        {...question}
                        autoSelect={isAutoSelect(question)}
                        flow={flow}
                        extraData={{
                            user: extraData.user,
                            hire_id: extraData.hire_id,
                            checkin_id: extraData.checkin_id,
                        }}
                        handleAnswer={handleAnswer}
                        handleSkip={onSkip}
                        handleFollowUpStart={handleFollowUpStart}
                        toggleAnonymous={toggleAnonymous}
                        isKiosk={!!isKiosk}
                        isLastQuestion={isLastQuestion}
                        isAnonymous={isAnonymous}
                    />
                </Card>
            </CSSTransition>
        ));
    };

    const localRenderLanguageSelect = () => {
        if (renderLanguageSelect) {
            return renderLanguageSelect();
        }
        return null;
    };

    const renderNext = () => {
        if (questions.length === 0 || isAutoSelect()) {
            return null;
        }

        const classes = classNames("btn btn-primary btn-large", {
            loading: submitting,
        });
        const btnDisabled = !currentAnswer;

        return (
            <div className="questions__next">
                <button
                    className={classes}
                    data-test="next-button"
                    disabled={btnDisabled}
                    onClick={onNext}
                >
                    {isLastQuestion ? _("Send") : _("Next")}
                </button>
            </div>
        );
    };

    const renderLoader = () => {
        if (!isAutoSelect() || !submitting) {
            return null;
        }

        return (
            <CSSTransition mountOnEnter unmountOnExit classNames="fadeout" timeout={100}>
                <Loader />
            </CSSTransition>
        );
    };

    const renderError = () => {
        if (!asyncError) {
            return null;
        }

        return (
            <CSSTransition mountOnEnter unmountOnExit classNames="fadeout" timeout={100}>
                <div className="questions__error">
                    <span>{_("Oops! Looks like we encountered an error. Please try again.")}</span>
                    <button onClick={onDismissError} className="btn btn-primary btn-large">
                        {_("Okay")}
                    </button>
                </div>
            </CSSTransition>
        );
    };

    return (
        <div className={className}>
            <CardGroup className="questions__card-group">
                {renderLoader()}
                {renderQuestions()}
                {renderUndo()}
                {renderError()}
            </CardGroup>
            {localRenderLanguageSelect()}
            {renderNext()}
        </div>
    );
};

Questions.contextTypes = {
    t: PropTypes.func,
};

Questions.propTypes = {
    // Whether answers are submitted immediately when an option is selected
    autoSelect: PropTypes.bool,
    // Whether skip button is shown
    canSkip: PropTypes.bool,
    canUndo: PropTypes.bool,
    className: PropTypes.string,
    extraData: PropTypes.shape({
        user: userShape,
        hire_id: PropTypes.number,
        checkin_id: PropTypes.number,
    }),
    flow: PropTypes.oneOf(_values(QUESTION_FLOW)).isRequired,
    handleCompleted: PropTypes.func,
    renderEmpty: PropTypes.func,
    source: PropTypes.number.isRequired,
    renderLanguageSelect: PropTypes.func,
    toggleAnonymous: PropTypes.func,
    handleAnswerResponse: PropTypes.func,
    isLastQuestion: PropTypes.bool,
    isAnonymous: PropTypes.bool.isRequired,
};

Questions.defaultProps = {
    autoSelect: false,
    canSkip: false,
    canUndo: false,
    className: "",
    extraData: {},
    handleCompleted: null,
    renderEmpty: null,
    renderLanguageSelect: null,
    toggleAnonymous: null,
    handleAnswerResponse: null,
    isLastQuestion: false,
};

export default React.memo(Questions);
